import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Send, Trash2, Bot, Sparkles, MessagesSquare, Plus, X, Paperclip, FileText, Loader2, Search, Mic, Square, Volume2, VolumeX } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import {
  listChatSessions,
  getChatSession,
  deleteChatSession,
  createChatSession,
  searchChatSessions,
  aiOrchestrator,
} from "@/lib/ai-helper.functions";
import { transcribeVoice } from "@/lib/dar.functions";
import { speakText } from "@/lib/voice-chat.functions";
import { DarRecorder, blobToBase64 } from "@/lib/dar-recorder";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const MODEL_OPTIONS = [
  { key: "auto", label: "Default", hint: "Balanced Mission Ready assistant with live data tools" },
  { key: "deepseek-r1-14b", label: "DeepSeek-R1 14B", hint: "Deeper reasoning, slower (no live data tools)" },
  { key: "qwen3-8b", label: "Qwen 8B", hint: "Fast, natural chat (no live data tools)" },
] as const;

type ModelKey = (typeof MODEL_OPTIONS)[number]["key"];

type SessionSummary = {
  id: string;
  title?: string | null;
  organization_id?: string | null;
  last_accessed: string | null;
  created_at: string | null;
  context: any;
};


export const Route = createFileRoute("/_authenticated/ai-helper")({
  component: AIHelperPage,
  head: () => ({ meta: [{ title: "AI Helper — MissionReady" }] }),
});

type ChatAttachment = {
  url: string;
  mime_type: string;
  name?: string;
  size?: number;
};

type VoiceMeta = {
  transcript: string;
  duration_sec?: number;
  recorded_at?: string;
};

type ChatMsg = {
  role: "user" | "assistant" | "tool" | "system";
  content: string | null;
  tool_calls?: any[];
  tool_call_id?: string;
  name?: string;
  attachments?: ChatAttachment[];
  voice?: VoiceMeta;
};

const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024; // 15 MB

function sessionLabel(s: SessionSummary): string {
  if (typeof s.title === "string" && s.title.trim()) return s.title.trim();
  const ctxTitle =
    s.context && typeof s.context === "object" && typeof s.context.title === "string"
      ? s.context.title.trim()
      : "";
  if (ctxTitle) return ctxTitle;
  return "Conversation";
}


function formatTimestamp(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
  if (diffDays < 7) {
    return d.toLocaleDateString(undefined, { weekday: "short" });
  }
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function AIHelperPage() {
  const { user, currentRole, isPlatformAdmin, memberships, currentOrg } = useAuth();
  // Callbacks below are memoized without `currentOrg` in their deps; a ref keeps
  // them reading the *live* active org so chats never land in a stale/other org.
  const orgIdRef = useRef<string | null>(currentOrg?.id ?? null);
  useEffect(() => {
    orgIdRef.current = currentOrg?.id ?? null;

  }, [currentOrg?.id]);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<ChatAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SessionSummary[]>([]);
  const [searching, setSearching] = useState(false);
  const [modelKey, setModelKey] = useState<ModelKey>("auto");

  // ---- Voice chatbot state (speak a question, hear the answer) ----
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const recorderRef = useRef<DarRecorder | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const voiceModeRef = useRef(false);
  useEffect(() => {
    voiceModeRef.current = voiceMode;
    try {
      localStorage.setItem("mr_ai_voice_mode", voiceMode ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [voiceMode]);
  useEffect(() => {
    try {
      if (localStorage.getItem("mr_ai_voice_mode") === "1") setVoiceMode(true);
    } catch {
      /* ignore */
    }
    return () => {
      recorderRef.current?.cancel();
      audioRef.current?.pause();
    };
  }, []);

  const speechQueueRef = useRef<string[]>([]);
  const drainingRef = useRef(false);
  const speechCancelledRef = useRef(false);

  const stopSpeaking = useCallback(() => {
    speechCancelledRef.current = true;
    speechQueueRef.current = [];
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.src = "";
    }
    setSpeaking(false);
  }, []);

  /** Play one chunk of speech and resolve when it finishes. */
  const playChunk = useCallback(async (text: string) => {
    const res = await speakText({ data: { text, voice: "thalia" } });
    if (!res.ok) {
      if (res.error === "tts_not_configured") toast.error("Voice replies aren't configured yet.");
      return;
    }
    if (speechCancelledRef.current) return;
    const el = audioRef.current ?? new Audio();
    audioRef.current = el;
    el.src = `data:${res.mimeType};base64,${res.audioBase64}`;
    setSpeaking(true);
    await new Promise<void>((resolve) => {
      el.onended = () => resolve();
      el.onerror = () => resolve();
      el.play().catch(() => resolve());
    });
  }, []);

  /** Drain queued sentences one after another so speech tracks the stream. */
  const drainSpeech = useCallback(async () => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    try {
      while (speechQueueRef.current.length && !speechCancelledRef.current) {
        const next = speechQueueRef.current.shift()!;
        await playChunk(next);
      }
    } catch (e) {
      console.error("[ai-helper] speak failed", e);
    } finally {
      drainingRef.current = false;
      if (!speechQueueRef.current.length) setSpeaking(false);
    }
  }, [playChunk]);

  /**
   * Queue a piece of the answer for speech. Called repeatedly while the reply
   * streams in, so the voice starts talking before the text is finished.
   */
  const enqueueSpeech = useCallback(
    (text: string) => {
      const clean = text.trim();
      if (!clean) return;
      speechCancelledRef.current = false;
      speechQueueRef.current.push(clean);
      void drainSpeech();
    },
    [drainSpeech],
  );

  /** Speak a complete assistant answer (used for non-streamed replies). */
  const speakAnswer = useCallback(
    async (text: string) => {
      enqueueSpeech(text);
    },
    [enqueueSpeech],
  );




  // Restore the user's last model pick.
  useEffect(() => {
    try {
      const saved = localStorage.getItem("mr_ai_model") as ModelKey | null;
      if (saved && MODEL_OPTIONS.some((o) => o.key === saved)) setModelKey(saved);
    } catch {
      /* ignore */
    }
  }, []);
  const handleModelChange = useCallback((key: ModelKey) => {
    setModelKey(key);
    try {
      localStorage.setItem("mr_ai_model", key);
    } catch {
      /* ignore */
    }
  }, []);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const prevMessagesLenRef = useRef(0);
  const prevSendingRef = useRef(false);

  const handleFilesSelected = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      if (!user?.id) {
        toast.error("Please sign in again to upload.");
        return;
      }
      const room = MAX_ATTACHMENTS - pendingAttachments.length;
      if (room <= 0) {
        toast.error(`You can attach up to ${MAX_ATTACHMENTS} files per message.`);
        return;
      }
      const list = Array.from(files).slice(0, room);
      setUploading(true);
      try {
        const uploaded: ChatAttachment[] = [];
        for (const file of list) {
          if (file.size > MAX_ATTACHMENT_BYTES) {
            toast.error(`${file.name} is larger than 15 MB.`);
            continue;
          }
          const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
          const path = `${user.id}/${sessionId ?? "tmp"}/${Date.now()}-${safeName}`;
          const { error } = await supabase.storage
            .from("chat-attachments")
            .upload(path, file, {
              contentType: file.type || "application/octet-stream",
              upsert: false,
            });
          if (error) {
            console.error("[ai-helper] upload failed", error);
            toast.error(`Couldn't upload ${file.name}.`);
            continue;
          }
          const { data: signed } = await supabase.storage
            .from("chat-attachments")
            .createSignedUrl(path, 60 * 60 * 24 * 7);
          uploaded.push({
            url: signed?.signedUrl ?? "",
            mime_type: file.type || "application/octet-stream",
            name: file.name,
            size: file.size,
          });
        }
        if (uploaded.length > 0) {
          setPendingAttachments((prev) => [...prev, ...uploaded]);
        }
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [pendingAttachments.length, sessionId, user?.id],
  );

  const removePendingAttachment = useCallback((url: string) => {
    setPendingAttachments((prev) => prev.filter((a) => a.url !== url));
  }, []);


  const refreshSessions = useCallback(async () => {
    try {
      const res = await listChatSessions({ data: { organization_id: orgIdRef.current } });
      if (res.ok) setSessions(res.sessions as SessionSummary[]);
    } catch (e) {
      console.error("[ai-helper] list sessions failed", e);
    }
  }, [currentOrg?.id]);

  // Initial load: pick most recent session for the active organization
  useEffect(() => {
    let cancelled = false;
    // Clear any conversation from the previously selected organization.
    setSessionId(null);
    setMessages([]);
    (async () => {

      try {
        const res = await listChatSessions({ data: { organization_id: orgIdRef.current } });
        if (cancelled) return;
        if (res.ok) {
          setSessions(res.sessions as SessionSummary[]);
          if (res.sessions.length > 0) {
            const sid = res.sessions[0].id as string;
            const detail = await getChatSession({ data: { session_id: sid, organization_id: orgIdRef.current } });
            if (!cancelled && detail.ok) {
              setSessionId(sid);
              setMessages(
                ((detail.session.messages ?? []) as ChatMsg[]).filter(
                  (m) => m.role === "user" || m.role === "assistant",
                ),
              );
            }
          }
        }
      } catch (e) {
        console.error("[ai-helper] load failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Conversation history is scoped per organization, so reload on org switch.
  }, [currentOrg?.id]);

  // Realtime: subscribe to chat_sessions changes for this user
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`chat_sessions:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "chat_sessions",
          filter: `user_id=eq.${user.id}`,
        },
        (payload: any) => {
          const row = payload.new ?? payload.old;
          if (!row) return;
          // Keep the session list fresh on any insert/update/delete
          refreshSessions();
          if (sessionId && row.id === sessionId && payload.new?.messages) {
            const visible = (payload.new.messages as ChatMsg[]).filter(
              (m) => m.role === "user" || m.role === "assistant",
            );
            setMessages(visible);
          }
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, sessionId, refreshSessions]);

  // Track scroll position to know if user is near bottom
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const threshold = 120; // px from bottom
      isNearBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
      if (isNearBottomRef.current) setHasNewMessages(false);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // Auto-scroll only when user is already near bottom (or just sent a message)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const messagesGrew = messages.length > prevMessagesLenRef.current;
    const sendingStopped = prevSendingRef.current && !sending;
    prevMessagesLenRef.current = messages.length;
    prevSendingRef.current = sending;

    if (messagesGrew && !isNearBottomRef.current) {
      setHasNewMessages(true);
      return;
    }

    // Always scroll on fresh load, after sending stops, or when near bottom
    if (isNearBottomRef.current || sendingStopped || (!loading && messages.length <= 2)) {
      requestAnimationFrame(() => {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      });
      setHasNewMessages(false);
    }
  }, [messages, sending, loading]);

  // Observe layout shifts (images loading, markdown rendering) and scroll if near bottom
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const observer = new MutationObserver(() => {
      if (isNearBottomRef.current) {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      }
    });
    observer.observe(el, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Debounced search across sessions
  useEffect(() => {
    const query = searchQuery.trim();
    if (!query) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await searchChatSessions({ data: { query, organization_id: orgIdRef.current } });
        if (res.ok) setSearchResults(res.sessions as SessionSummary[]);
      } catch (e) {
        console.error("[ai-helper] search failed", e);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSend = useCallback(
    async (override?: string, voice?: VoiceMeta) => {
      const text = (override ?? input).trim();
      const attachments = override ? [] : pendingAttachments;
      if ((!text && attachments.length === 0) || sending) return;
      if (!override) {
        setInput("");
        setPendingAttachments([]);
      }
      const userMsg: ChatMsg = {
        role: "user",
        content: text,
        attachments: attachments.length > 0 ? attachments : undefined,
        voice,
      };
      // Optimistic user turn + an empty assistant bubble the stream fills in.
      setMessages((prev) => [...prev, userMsg, { role: "assistant", content: "" }]);
      setSending(true);
      setStreaming(false);
      speechCancelledRef.current = false;

      /** Replace the trailing assistant bubble with the streamed text so far. */
      const paintAssistant = (content: string) => {
        setMessages((prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i--) {
            if (next[i].role === "assistant") {
              next[i] = { ...next[i], content };
              break;
            }
          }
          return next;
        });
      };

      const payload = {
        session_id: sessionId ?? undefined,
        model_choice: modelKey,
        organization_id: orgIdRef.current,
        messages: [
          {
            role: "user" as const,
            content: text,
            ...(attachments.length > 0 ? { attachments } : {}),
            ...(voice ? { voice } : {}),
          },
        ],
      };

      /**
       * If the streaming endpoint can't be used (expired token, edge/proxy
       * hiccup, non-2xx), fall back to the non-streaming orchestrator so the
       * user still gets their answer instead of a dead-end error toast.
       */
      const runFallback = async (): Promise<boolean> => {
        try {
          const res: any = await aiOrchestrator({ data: payload });
          if (!res?.ok) {
            const error = res?.error;
            toast.error(
              error === "rate_limited"
                ? "Rate limited — please wait a moment."
                : error === "payment_required"
                  ? "AI credits exhausted."
                  : "AI Helper failed to respond.",
            );
            return false;
          }
          if (res.session_id) setSessionId(res.session_id);
          const visible = ((res.messages ?? []) as ChatMsg[]).filter(
            (m) => m.role === "user" || m.role === "assistant",
          );
          if (visible.length) setMessages(visible);
          else if (res.assistant?.content) paintAssistant(String(res.assistant.content));
          const answerText =
            [...visible].reverse().find((m) => m.role === "assistant")?.content ??
            res.assistant?.content;
          if (voiceModeRef.current && answerText) void speakAnswer(String(answerText));
          return true;
        } catch (e) {
          console.error("[ai-helper] fallback failed", e);
          toast.error("Couldn't reach the AI Helper.");
          return false;
        }
      };

      try {
        // Refresh first: a stale access token is the most common cause of a
        // 401 from the streaming route.
        let accessToken = (await supabase.auth.getSession()).data.session?.access_token;
        if (!accessToken) {
          accessToken = (await supabase.auth.refreshSession()).data.session?.access_token;
        }
        if (!accessToken) {
          await runFallback();
          return;
        }

        const resp = await fetch("/api/ai-helper-stream", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        });

        if (!resp.ok || !resp.body) {
          console.error("[ai-helper] stream endpoint failed", resp.status);
          await runFallback();
          return;
        }

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let answer = "";
        let spokenUpTo = 0;
        let done: any = null;

        // Speak complete sentences as they arrive so the voice keeps pace.
        const flushSpeech = (final: boolean) => {
          if (!voiceModeRef.current) return;
          const pending = answer.slice(spokenUpTo);
          if (final) {
            if (pending.trim()) {
              enqueueSpeech(pending);
              spokenUpTo = answer.length;
            }
            return;
          }
          const match = /^[\s\S]*?[.!?…](\s|$)/.exec(pending);
          if (match && match[0].trim().length > 24) {
            enqueueSpeech(match[0]);
            spokenUpTo += match[0].length;
          }
        };

        const handleLine = (line: string) => {
          const trimmed = line.trim();
          if (!trimmed) return;
          let evt: any;
          try {
            evt = JSON.parse(trimmed);
          } catch {
            return;
          }
          if (evt.type === "delta" && typeof evt.text === "string") {
            answer += evt.text;
            setStreaming(true);
            paintAssistant(answer);
            flushSpeech(false);
          } else if (evt.type === "done") {
            done = evt;
          }
        };

        while (true) {
          const { done: finished, value } = await reader.read();
          if (finished) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";
          for (const line of lines) handleLine(line);
        }
        if (buffer) handleLine(buffer);

        if (!done || !done.ok) {
          const error = done?.error;
          toast.error(
            error === "rate_limited"
              ? "Rate limited — please wait a moment."
              : error === "payment_required"
                ? "AI credits exhausted."
                : error === "hf_token_not_configured"
                  ? "Hugging Face token isn't configured yet."
                  : "AI Helper failed to respond.",
          );
          return;
        }

        if (done.session_id) setSessionId(done.session_id);
        const visible = ((done.messages ?? []) as ChatMsg[]).filter(
          (m) => m.role === "user" || m.role === "assistant",
        );
        if (visible.length) setMessages(visible);
        flushSpeech(true);
        // Fallback: nothing streamed (e.g. tool-only hops) but we have an answer.
        if (voiceModeRef.current && !answer.trim()) {
          const lastAssistant = [...visible].reverse().find((m) => m.role === "assistant");
          if (lastAssistant?.content) void speakAnswer(String(lastAssistant.content));
        }
      } catch (e: any) {
        console.error("[ai-helper] send failed", e);
        toast.error("Couldn't reach the AI Helper.");
      } finally {
        setStreaming(false);
        setSending(false);
      }
    },
    [input, pendingAttachments, sending, sessionId, modelKey, speakAnswer, enqueueSpeech],

  );

  /** Press-to-talk: record, transcribe with Deepgram, then ask the workspace AI. */
  const startRecording = useCallback(async () => {
    if (recording || sending || transcribing) return;
    stopSpeaking();
    try {
      const rec = new DarRecorder();
      await rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (e: any) {
      console.error("[ai-helper] mic failed", e);
      toast.error(e?.message ?? "Couldn't access the microphone.");
    }
  }, [recording, sending, transcribing, stopSpeaking]);

  const cancelRecording = useCallback(() => {
    recorderRef.current?.cancel();
    recorderRef.current = null;
    setRecording(false);
  }, []);

  const stopAndSend = useCallback(async () => {
    const rec = recorderRef.current;
    if (!rec) return;
    recorderRef.current = null;
    setRecording(false);
    setTranscribing(true);
    try {
      const out = await rec.stop();
      if (out.blob.size < 1200) {
        toast.error("That recording was too short.");
        return;
      }
      const audioBase64 = await blobToBase64(out.blob);
      const res = await transcribeVoice({ data: { audioBase64, mimeType: out.mimeType } });
      const text = (res?.text ?? "").trim();
      if (!text) {
        toast.error("Couldn't hear anything — try again.");
        return;
      }
      if (!voiceModeRef.current) setVoiceMode(true);
      voiceModeRef.current = true;
      // Save the transcript with the turn so the stored history shows exactly
      // what was spoken, how long the memo was, and when it was recorded.
      await handleSend(text, {
        transcript: text,
        duration_sec: out.durationSec ?? undefined,
        recorded_at: out.startedAt ?? new Date().toISOString(),
      });
    } catch (e: any) {
      console.error("[ai-helper] voice question failed", e);
      toast.error("Voice question failed. Please try again.");
    } finally {
      setTranscribing(false);
    }
  }, [handleSend]);


  const handleClear = useCallback(async () => {
    if (!sessionId) {
      setMessages([]);
      return;
    }
    if (!confirm("Clear this conversation?")) return;
    const res = await deleteChatSession({ data: { session_id: sessionId, organization_id: orgIdRef.current } });
    if (res.ok) {
      setSessionId(null);
      setMessages([]);
      toast.success("Conversation cleared.");
    } else {
      toast.error("Couldn't clear chat.");
    }
  }, [sessionId]);

  const handleNewChat = useCallback(async () => {
    setSwitcherOpen(false);
    setInput("");
    setPendingAttachments([]);
    setMessages([]);
    try {
      const res = await createChatSession({ data: { organization_id: orgIdRef.current } });
      if (res.ok && res.session) {
        setSessionId(res.session.id as string);
        setSessions((prev) => [
          {
            id: res.session!.id as string,
            last_accessed: (res.session!.last_accessed as string) ?? null,
            created_at: (res.session!.created_at as string) ?? null,
            context: res.session!.context ?? null,
          },
          ...prev,
        ]);
      } else {
        toast.error("Couldn't start a new chat.");
        setSessionId(null);
      }
    } catch (e) {
      console.error("[ai-helper] create session failed", e);
      toast.error("Couldn't start a new chat.");
      setSessionId(null);
    }
  }, []);

  const handleSelectSession = useCallback(async (sid: string) => {
    if (loadingSession) return;
    setSwitcherOpen(false);
    setLoadingSession(true);
    try {
      const detail = await getChatSession({ data: { session_id: sid, organization_id: orgIdRef.current } });
      if (detail.ok) {
        setSessionId(sid);
        setMessages(
          ((detail.session.messages ?? []) as ChatMsg[]).filter(
            (m) => m.role === "user" || m.role === "assistant",
          ),
        );
      } else {
        toast.error("Couldn't load that conversation.");
      }
    } catch (e) {
      console.error("[ai-helper] load session failed", e);
      toast.error("Couldn't load that conversation.");
    } finally {
      setLoadingSession(false);
    }
  }, [loadingSession]);

  const handleDeleteSession = useCallback(
    async (sid: string) => {
      if (!confirm("Delete this conversation?")) return;
      const res = await deleteChatSession({ data: { session_id: sid, organization_id: orgIdRef.current } });
      if (!res.ok) {
        toast.error("Couldn't delete chat.");
        return;
      }
      toast.success("Conversation deleted.");
      setSessions((prev) => prev.filter((s) => s.id !== sid));
      if (sid === sessionId) {
        setSessionId(null);
        setMessages([]);
      }
    },
    [sessionId],
  );

  const roleLabel = useMemo(() => {
    if (isPlatformAdmin) return "Platform";
    const r = (currentRole ?? "guard").toLowerCase();
    // Solo contractor heuristic: only member of their org and owner role
    const isSolo =
      memberships.length === 1 &&
      ["owner", "org_owner"].includes(r) &&
      // Single-seat orgs typically have no other members; we approximate by role
      true === true;
    if (["admin", "owner", "org_owner"].includes(r)) {
      return isSolo ? "Solo" : "Admin";
    }
    if (["supervisor", "site_supervisor", "dispatcher"].includes(r))
      return "Supervisor";
    return "Field";
  }, [currentRole, isPlatformAdmin, memberships.length]);

  const promptChips = useMemo(() => {
    if (roleLabel === "Platform") {
      return [
        { label: "Platform overview", prompt: "Give me a platform activity overview for the last 24 hours." },
        { label: "Active organizations", prompt: "Show all active organizations and their current status." },
        { label: "Recent platform alerts", prompt: "Show recent platform-wide alerts and incidents." },
        { label: "Top orgs by activity", prompt: "Which organizations are most active this week?" },
        { label: "Health check", prompt: "Any orgs with no activity in the past 7 days?" },
      ];
    }
    if (roleLabel === "Solo") {
      return [
        { label: "Log my hours", prompt: "Clock me in for my shift." },
        { label: "Start a guard tour", prompt: "How do I start a guard tour?" },
        { label: "File an incident", prompt: "Help me submit an incident report." },
        { label: "My schedule", prompt: "What's my schedule for this week?" },
        { label: "Export my hours", prompt: "How do I export my hours for invoicing?" },
      ];
    }
    if (roleLabel === "Admin") {
      return [
        { label: "Team performance", prompt: "How is the team performing this week? Highlight any concerns." },
        { label: "Incident trends", prompt: "Summarize incident trends across all sites for the last 7 days." },
        { label: "Coverage gaps", prompt: "Are there any shift coverage gaps in the next 24 hours?" },
        { label: "Open follow-ups", prompt: "List all open incident follow-ups assigned to my organization." },
        { label: "Message night shift", prompt: "Send a message to the night shift team to double-check perimeter checkpoints." },
      ];
    }
    if (roleLabel === "Supervisor") {
      return [
        { label: "Team performance", prompt: "How is my team performing this week?" },
        { label: "Recent incidents", prompt: "Show me recent incidents at my site." },
        { label: "Who's on shift?", prompt: "Who is currently on shift right now?" },
        { label: "Team schedule", prompt: "Show my team's schedule for today and tomorrow." },
        { label: "Message my team", prompt: "Send a message to my team reminding them about today's briefing." },
      ];
    }
    return [
      { label: "Start a guard tour", prompt: "How do I start a guard tour?" },
      { label: "File an incident report", prompt: "How do I file an incident report?" },
      { label: "How do I clock in?", prompt: "How do I clock in?" },
      { label: "My schedule", prompt: "What's my schedule for today?" },
      { label: "Next shift", prompt: "When's my next shift?" },
    ];
  }, [roleLabel]);

  const handleChipClick = useCallback(
    (prompt: string) => {
      if (sending) return;
      void handleSend(prompt);
    },
    [sending, handleSend],
  );

  return (
    <div className="flex h-[calc(100dvh-3.5rem)] md:h-[calc(100dvh-4rem)] flex-col bg-[#111111] text-white">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-white/10 bg-[#0D0D0D] px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#CC0000]/15 text-[#CC0000]">
            <Bot className="h-5 w-5" />
          </div>
          <div>
            <h1
              className="text-lg font-black tracking-[0.18em] leading-none"
              style={{ fontFamily: "'Orbitron', system-ui, sans-serif" }}
            >
              AI <span className="text-[#CC0000]">HELPER</span>
            </h1>
            <p className="mt-1 text-[10px] uppercase tracking-widest text-white/40">
              Mission Ready · {roleLabel} mode
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Sheet
            open={switcherOpen}
            onOpenChange={(open) => {
              setSwitcherOpen(open);
              if (open) refreshSessions();
            }}
          >
            <SheetTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Open conversations"
                className="text-white/60 hover:text-[#CC0000] hover:bg-white/5"
              >
                <MessagesSquare className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[88vw] max-w-sm border-white/10 bg-[#0D0D0D] p-0 text-white"
            >
              <SheetHeader className="border-b border-white/10 px-4 py-4 text-left">
                <SheetTitle
                  className="text-sm font-bold tracking-[0.18em] text-white"
                  style={{ fontFamily: "'Orbitron', system-ui, sans-serif" }}
                >
                  CONVERSATIONS
                </SheetTitle>
              </SheetHeader>
              <div className="px-3 py-3">
                <button
                  type="button"
                  onClick={handleNewChat}
                  className="flex w-full items-center gap-2 rounded-lg border border-[#CC0000]/40 bg-[#CC0000]/10 px-3 py-2.5 text-left text-sm font-medium text-white transition-colors hover:bg-[#CC0000]/20"
                >
                  <Plus className="h-4 w-4 text-[#CC0000]" />
                  New conversation
                </button>
              </div>
              <div className="px-3 pb-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/30" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search conversations…"
                    className="w-full rounded-md border border-white/10 bg-[#1A1A1A] py-2 pl-8 pr-8 text-xs text-white placeholder:text-white/30 focus:border-[#CC0000]/60 focus:outline-none"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-3 pb-6">
                {searching ? (
                  <div className="flex items-center justify-center gap-2 py-6 text-xs text-white/40">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Searching…
                  </div>
                ) : searchQuery.trim() ? (
                  searchResults.length === 0 ? (
                    <p className="px-1 py-6 text-center text-xs text-white/40">
                      No conversations match "{searchQuery.trim()}".
                    </p>
                  ) : (
                    <ul className="flex flex-col gap-1">
                      {searchResults.map((s) => {
                        const isActive = s.id === sessionId;
                        return (
                          <li key={s.id}>
                            <div
                              className={cn(
                                "group flex items-center gap-1 rounded-lg border px-2 py-2 transition-colors",
                                isActive
                                  ? "border-[#CC0000]/50 bg-[#CC0000]/10"
                                  : "border-white/5 bg-[#1A1A1A]/60 hover:border-white/15 hover:bg-[#1A1A1A]",
                              )}
                            >
                              <button
                                type="button"
                                onClick={() => handleSelectSession(s.id)}
                                className="min-w-0 flex-1 text-left"
                              >
                                <p className="truncate text-xs font-medium text-white/90">
                                  {sessionLabel(s)}
                                </p>
                                <p className="mt-0.5 text-[10px] text-white/40">
                                  {formatTimestamp(s.last_accessed ?? s.created_at)}
                                </p>
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteSession(s.id);
                                }}
                                aria-label="Delete conversation"
                                className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-white/30 opacity-0 transition-opacity hover:bg-white/5 hover:text-[#CC0000] group-hover:opacity-100 focus:opacity-100"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )
                ) : sessions.length === 0 ? (
                  <p className="px-1 py-6 text-center text-xs text-white/40">
                    No previous conversations.
                  </p>
                ) : (
                  <ul className="flex flex-col gap-1">
                    {sessions.map((s) => {
                      const isActive = s.id === sessionId;
                      return (
                        <li key={s.id}>
                          <div
                            className={cn(
                              "group flex items-center gap-1 rounded-lg border px-2 py-2 transition-colors",
                              isActive
                                ? "border-[#CC0000]/50 bg-[#CC0000]/10"
                                : "border-white/5 bg-[#1A1A1A]/60 hover:border-white/15 hover:bg-[#1A1A1A]",
                            )}
                          >
                            <button
                              type="button"
                              onClick={() => handleSelectSession(s.id)}
                              className="min-w-0 flex-1 text-left"
                            >
                              <p className="truncate text-xs font-medium text-white/90">
                                {sessionLabel(s)}
                              </p>
                              <p className="mt-0.5 text-[10px] text-white/40">
                                {formatTimestamp(s.last_accessed ?? s.created_at)}
                              </p>
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSession(s.id);
                              }}
                              aria-label="Delete conversation"
                              className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-white/30 opacity-0 transition-opacity hover:bg-white/5 hover:text-[#CC0000] group-hover:opacity-100 focus:opacity-100"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </SheetContent>
          </Sheet>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleNewChat}
            aria-label="New conversation"
            className="text-white/60 hover:text-[#CC0000] hover:bg-white/5"
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleClear}
            aria-label="Clear conversation"
            className="text-white/60 hover:text-[#CC0000] hover:bg-white/5"
            disabled={messages.length === 0 && !sessionId}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* Model selector */}
      <div className="border-b border-white/5 bg-[#0D0D0D]/80">
        <div className="mx-auto flex max-w-2xl items-center gap-2 px-3 py-2 sm:px-6">
          <span className="shrink-0 text-[10px] uppercase tracking-widest text-white/35">
            Model
          </span>
          <div
            role="radiogroup"
            aria-label="Model selector"
            className="flex min-w-0 flex-1 gap-1 overflow-x-auto rounded-lg border border-white/10 bg-[#141414] p-1"
            style={{ scrollbarWidth: "none" }}
          >
            {MODEL_OPTIONS.map((o) => (
              <button
                key={o.key}
                type="button"
                role="radio"
                aria-checked={modelKey === o.key}
                title={o.hint}
                onClick={() => handleModelChange(o.key)}
                disabled={sending}
                className={cn(
                  "shrink-0 rounded-md px-2.5 py-1.5 text-[11px] font-semibold tracking-wide transition-colors disabled:opacity-40",
                  modelKey === o.key
                    ? "bg-[#CC0000] text-white"
                    : "text-white/60 hover:bg-white/5 hover:text-white",
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Suggested prompt chips */}
      <div className="border-b border-white/5 bg-[#0D0D0D]/80">
        <div
          className="mx-auto flex max-w-2xl gap-2 overflow-x-auto px-3 py-2 sm:px-6"
          style={{ scrollbarWidth: "none" }}
          aria-label="Suggested prompts"
        >
          {promptChips.map((chip) => (
            <button
              key={chip.label}
              type="button"
              onClick={() => handleChipClick(chip.prompt)}
              disabled={sending}
              className="shrink-0 rounded-full border border-white/10 bg-[#1A1A1A] px-3 py-1.5 text-xs font-medium text-white/80 transition-colors hover:border-[#CC0000]/60 hover:bg-[#CC0000]/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Sparkles className="mr-1 inline h-3 w-3 text-[#CC0000]" />
              {chip.label}
            </button>
          ))}
        </div>
      </div>

      {/* Message area */}
      <div className="relative flex-1">
        <div
          ref={scrollRef}
          className="h-full overflow-y-auto px-3 py-4 sm:px-6"
          style={{ scrollbarGutter: "stable" }}
        >
          <div className="mx-auto flex max-w-2xl flex-col gap-3">
            {loading && (
              <div className="flex justify-center py-8 text-xs text-white/40">
                Loading conversation…
              </div>
            )}

            {!loading && messages.length === 0 && (
              <EmptyState role={roleLabel} />
            )}

            {messages.map((m, i) => (
              <MessageBubble
                key={i}
                role={m.role as "user" | "assistant"}
                content={m.content ?? ""}
                attachments={m.attachments}
                voice={m.voice}
                isLast={i === messages.length - 1}
              />
            ))}

            {sending && !streaming && <TypingIndicator />}
          </div>
        </div>

        {/* Scroll-to-bottom badge */}
        {hasNewMessages && (
          <button
            type="button"
            onClick={() => {
              const el = scrollRef.current;
              if (!el) return;
              isNearBottomRef.current = true;
              setHasNewMessages(false);
              el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
            }}
            className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 flex items-center gap-2 rounded-full border border-[#CC0000]/40 bg-[#CC0000]/90 px-4 py-2 text-xs font-semibold text-white shadow-lg shadow-[#CC0000]/30 backdrop-blur-sm transition-all hover:scale-105 hover:bg-[#CC0000]"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
            </span>
            New message
          </button>
        )}
      </div>

      {/* Input bar */}
      <div
        className="border-t border-white/10 bg-[#0D0D0D] px-3 py-3 sm:px-6"
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      >
        <div className="mx-auto max-w-2xl">
          {(pendingAttachments.length > 0 || uploading) && (
            <div className="mb-2 flex flex-wrap items-center gap-2">
              {pendingAttachments.map((att) => (
                <div
                  key={att.url}
                  className="group relative flex items-center gap-2 rounded-lg border border-white/10 bg-[#1A1A1A] py-1 pl-1 pr-7 text-xs text-white/80"
                >
                  {att.mime_type.startsWith("image/") ? (
                    <img
                      src={att.url}
                      alt={att.name ?? "attachment"}
                      className="h-9 w-9 rounded object-cover"
                    />
                  ) : (
                    <span className="flex h-9 w-9 items-center justify-center rounded bg-black/40 text-[#CC0000]">
                      <FileText className="h-4 w-4" />
                    </span>
                  )}
                  <span className="max-w-[10rem] truncate">{att.name ?? "Attachment"}</span>
                  <button
                    type="button"
                    onClick={() => removePendingAttachment(att.url)}
                    aria-label={`Remove ${att.name ?? "attachment"}`}
                    className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded text-white/40 hover:bg-white/5 hover:text-[#CC0000]"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {uploading && (
                <span className="flex items-center gap-1.5 text-[11px] text-white/50">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Uploading…
                </span>
              )}
            </div>
          )}
          {/* Voice status strip */}
          {(recording || transcribing || speaking) && (
            <div className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-[#CC0000]/40 bg-[#CC0000]/10 px-3 py-2 text-xs text-white/80">
              <span className="flex items-center gap-2">
                {recording ? (
                  <>
                    <span className="h-2 w-2 animate-pulse rounded-full bg-[#CC0000]" />
                    Listening… tap the square to ask
                  </>
                ) : transcribing ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Transcribing your question…
                  </>
                ) : (
                  <>
                    <Volume2 className="h-3 w-3" />
                    Speaking the answer…
                  </>
                )}
              </span>
              {recording ? (
                <button type="button" onClick={cancelRecording} className="underline">
                  Cancel
                </button>
              ) : speaking ? (
                <button type="button" onClick={stopSpeaking} className="underline">
                  Stop
                </button>
              ) : null}
            </div>
          )}
          <form

            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.txt,.log,.csv,.json"
              className="hidden"
              onChange={(e) => handleFilesSelected(e.target.files)}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={
                sending ||
                uploading ||
                pendingAttachments.length >= MAX_ATTACHMENTS
              }
              aria-label="Attach files"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-[#1A1A1A] text-white/70 transition-colors hover:border-[#CC0000]/50 hover:text-[#CC0000] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Paperclip className="h-4 w-4" />
              )}
            </button>
            <button
              type="button"
              onClick={recording ? stopAndSend : startRecording}
              disabled={sending || transcribing}
              aria-label={recording ? "Stop and ask" : "Ask by voice"}
              className={cn(
                "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border transition-colors disabled:cursor-not-allowed disabled:opacity-40",
                recording
                  ? "border-[#CC0000] bg-[#CC0000] text-white"
                  : "border-white/10 bg-[#1A1A1A] text-white/70 hover:border-[#CC0000]/50 hover:text-[#CC0000]",
              )}
            >
              {transcribing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : recording ? (
                <Square className="h-4 w-4" />
              ) : (
                <Mic className="h-4 w-4" />
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                if (voiceMode) stopSpeaking();
                setVoiceMode((v) => !v);
              }}
              aria-label={voiceMode ? "Turn off spoken answers" : "Turn on spoken answers"}
              aria-pressed={voiceMode}
              title={voiceMode ? "Spoken answers on" : "Spoken answers off"}
              className={cn(
                "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border transition-colors",
                voiceMode
                  ? "border-[#CC0000]/60 bg-[#CC0000]/15 text-[#CC0000]"
                  : "border-white/10 bg-[#1A1A1A] text-white/50 hover:text-white/80",
              )}
            >
              {voiceMode ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </button>

            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              rows={1}
              placeholder="Ask anything — type your question or pick a suggestion above…"
              className="min-h-[44px] max-h-32 flex-1 resize-none rounded-xl border border-[#C0C0C0]/60 bg-[#1A1A1A] px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-[#CC0000] focus:outline-none focus:ring-2 focus:ring-[#CC0000]/40"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={
                sending ||
                (input.trim().length === 0 && pendingAttachments.length === 0)
              }
              aria-label="Send message"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#CC0000] text-white transition-colors hover:bg-[#E60000] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({
  role,
  content,
  attachments,
  voice,
  isLast,
}: {
  role: "user" | "assistant";
  content: string;
  attachments?: ChatAttachment[];
  voice?: VoiceMeta;
  isLast: boolean;
}) {
  const isUser = role === "user";
  const hasAttachments = !!attachments && attachments.length > 0;
  const voiceMeta = [
    voice?.duration_sec != null ? `${Math.max(1, Math.round(voice.duration_sec))}s` : null,
    voice?.recorded_at ? new Date(voice.recorded_at).toLocaleTimeString() : null,
  ].filter(Boolean);
  return (
    <div
      className={cn(
        "flex w-full",
        isUser ? "justify-end" : "justify-start",
      )}
    >
      <div
        className={cn(
          "group flex max-w-[85%] flex-col gap-1 sm:max-w-[75%]",
          isUser ? "items-end" : "items-start",
        )}
      >
        {hasAttachments && (
          <div
            className={cn(
              "flex flex-wrap gap-2",
              isUser ? "justify-end" : "justify-start",
            )}
          >
            {attachments!.map((att) => (
              <AttachmentPreview key={att.url} att={att} />
            ))}
          </div>
        )}
        {voice && (
          <div
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wide text-white/60",
              isUser ? "self-end" : "self-start",
            )}
            title="Saved voice transcript"
          >
            <Mic className="h-3 w-3" />
            <span>Voice transcript</span>
            {voiceMeta.length > 0 && <span className="normal-case tracking-normal">· {voiceMeta.join(" · ")}</span>}
          </div>
        )}
        {(content || !hasAttachments) && (
          <div
            className={cn(
              "rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm",
              isUser
                ? "rounded-br-md bg-[#CC0000] text-white"
                : "rounded-bl-md border border-white/5 bg-[#1A1A1A] text-white/90",
            )}
          >
            {isUser ? (
              <p className="whitespace-pre-wrap break-words">{content}</p>
            ) : (
              <div className="prose prose-sm prose-invert max-w-none break-words [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_code]:rounded [&_code]:bg-black/40 [&_code]:px-1 [&_code]:py-0.5">
                <ReactMarkdown>{content || "…"}</ReactMarkdown>
              </div>
            )}
          </div>
        )}
        {isUser && (
          <span className="px-1 text-[10px] text-white/30">
            {isLast ? "Delivered" : "Read"}
          </span>
        )}
      </div>
    </div>
  );
}

function AttachmentPreview({ att }: { att: ChatAttachment }) {
  const isImage = att.mime_type?.startsWith("image/");
  if (isImage) {
    return (
      <a
        href={att.url}
        target="_blank"
        rel="noreferrer"
        className="block overflow-hidden rounded-lg border border-white/10 bg-black/40"
      >
        <img
          src={att.url}
          alt={att.name ?? "attachment"}
          className="max-h-48 max-w-[14rem] object-cover"
          loading="lazy"
        />
      </a>
    );
  }
  return (
    <a
      href={att.url}
      target="_blank"
      rel="noreferrer"
      className="flex max-w-[14rem] items-center gap-2 rounded-lg border border-white/10 bg-[#1A1A1A] px-3 py-2 text-xs text-white/80 hover:border-[#CC0000]/50 hover:text-white"
    >
      <FileText className="h-4 w-4 shrink-0 text-[#CC0000]" />
      <span className="truncate">{att.name ?? "Attachment"}</span>
    </a>
  );
}

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="flex max-w-[85%] items-center gap-3 rounded-2xl rounded-bl-md border border-white/5 bg-[#1A1A1A] px-4 py-3 shadow-sm sm:max-w-[75%]">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#CC0000]/15">
          <Bot className="h-3.5 w-3.5 text-[#CC0000]" />
        </div>
        <div className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 animate-typing-wave rounded-full bg-[#CC0000]" style={{ animationDelay: "0ms" }} />
          <span className="h-1.5 w-1.5 animate-typing-wave rounded-full bg-[#CC0000]" style={{ animationDelay: "200ms" }} />
          <span className="h-1.5 w-1.5 animate-typing-wave rounded-full bg-[#CC0000]" style={{ animationDelay: "400ms" }} />
        </div>
        <span className="text-[11px] font-medium tracking-wide text-white/40">
          Thinking…
        </span>
      </div>
    </div>
  );
}

function EmptyState({ role }: { role: string }) {
  const suggestions =
    role === "Platform"
      ? [
          "Platform activity overview",
          "Show all active organizations",
          "Recent platform alerts",
        ]
      : role === "Admin"
        ? [
            "How is the team performing this week?",
            "Send a message to the night shift team",
            "Show incident trends for this site",
          ]
        : role === "Supervisor"
          ? [
              "View this team's schedule",
              "Send a message to my team",
              "Summarize today's incidents",
            ]
          : role === "Solo"
            ? [
                "Log my hours",
                "Start a guard tour",
                "File an incident report",
              ]
            : [
                "How do I clock in?",
                "How do I start a guard tour?",
                "How do I file an incident report?",
              ];
  return (
    <div className="flex flex-col items-center gap-4 py-12 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#CC0000]/15 text-[#CC0000]">
        <Sparkles className="h-7 w-7" />
      </div>
      <div>
        <h2
          className="text-base font-bold tracking-wider"
          style={{ fontFamily: "'Orbitron', system-ui, sans-serif" }}
        >
          HOW CAN I HELP?
        </h2>
        <p className="mt-1 text-xs text-white/50">
          Mission Ready operations assistant — tailored to your role.
        </p>
      </div>
      <div className="mt-2 flex w-full max-w-sm flex-col gap-2">
        {suggestions.map((s) => (
          <div
            key={s}
            className="rounded-lg border border-white/10 bg-[#1A1A1A]/60 px-3 py-2 text-left text-xs text-white/70"
          >
            {s}
          </div>
        ))}
      </div>
    </div>
  );
}
